/** Card — Spec 020 on the DS: surface, a hairline, radius 12. Plain, clickable, warning. */

import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { ChevronRight, Clock, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";

import { cn } from "@/lib/cn";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** 12 / 16 vertical in a list (DS `p-card`), 16 all round inside a sheet. */
  padding?: 12 | 16;
}

/** DS `p-card`: `surface`, 1px `border`, radius 12, padding 12 / 16. */
export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { className, padding = 12, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cn("rounded-card border border-p-border bg-p-surface", padding === 16 ? "p-4" : "px-4 py-3", className)}
      {...rest}
    />
  );
});

export interface ClickableCardProps {
  children: ReactNode;
  to?: string;
  onClick?: () => void;
  /** §7 — same card plus a chevron on the right. */
  chevron?: boolean;
  padding?: 12 | 16;
  /** §7 — the "done" treatment dims the whole card. */
  dimmed?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * §5 — the whole card is the tap target, not a button inside it. Rendered as
 * a link when it navigates so long-press and middle-click behave natively.
 */
export function ClickableCard({
  children,
  to,
  onClick,
  chevron = true,
  padding = 12,
  dimmed,
  className,
  ariaLabel,
}: ClickableCardProps) {
  const classes = cn(
    "press-surface flex w-full items-start gap-2.5 rounded-card border border-p-border bg-p-surface text-left",
    padding === 16 ? "p-4" : "px-4 py-3",
    dimmed && "done-dim",
    className,
  );

  const body = (
    <>
      <div className="min-w-0 flex-1">{children}</div>
      {chevron && (
        <ChevronRight className="mt-0.5 h-[18px] w-[18px] shrink-0 text-p-text-muted" aria-hidden="true" />
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} aria-label={ariaLabel}>
        {body}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes} aria-label={ariaLabel}>
      {body}
    </button>
  );
}

/**
 * The warning card: a record marked wrong. A strong hairline and a header in
 * the text colour carrying the reason; the body stays readable at full
 * contrast. Not red: in the brand that colour is the «Препятствие» status only
 * (Spec 020 D2), and a wrong record is not an obstacle. The icon and the
 * stronger line tell it from the stale card before a word is read.
 */
export function WarningCard({
  reason,
  children,
  className,
}: {
  reason: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("overflow-hidden rounded-card border border-p-border-strong", className)}>
      <div className="flex items-start gap-2 border-b border-p-border bg-p-surface-2 px-4 py-2">
        <TriangleAlert className="mt-px h-4 w-4 shrink-0 text-p-text" aria-hidden="true" />
        <p className="text-meta font-medium text-p-text">{reason}</p>
      </div>
      <div className="bg-p-surface px-4 py-3">{children}</div>
    </div>
  );
}

/**
 * FEAT-025 / addendum §2 — the *stale* card, sibling of WarningCard.
 *
 * A superseded journal entry is not a mistake: the decision was right when it
 * was made and the ground has since moved. So it gets the same anatomy as the
 * warning card at a lower volume — a `--sep` hairline instead of `--dstr`, a
 * `--sbg` header instead of a `--dstr-soft` wash, `clock` in `--hint` instead
 * of a red `triangle-alert`. Reading the two side by side has to tell you
 * which is which before you have read a word of the reason.
 *
 * The header sits on the same `--sbg` as the body (the addendum is explicit
 * about that), so a hairline under it does the separating — §4's rule that
 * every division in this product is 1px of `--sep`.
 */
export function StaleCard({
  reason,
  children,
  className,
}: {
  reason: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("overflow-hidden rounded-card border border-p-border", className)}>
      <div className="flex items-start gap-2 border-b border-p-border bg-p-surface px-4 py-2">
        <Clock className="mt-px h-4 w-4 shrink-0 text-p-text-muted" aria-hidden="true" />
        <p className="text-meta text-p-text-muted">{reason}</p>
      </div>
      <div className="bg-p-surface px-4 py-3">{children}</div>
    </div>
  );
}
